// src/pages/credit/CreditNoteForm.jsx
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import API_BASE_URL from "../../utils/config";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AsyncSelect from "react-select/async";

// ---- dev request logger ----
if (process.env.NODE_ENV !== "production" && !axios.__REQ_LOGGER__) {
  axios.__REQ_LOGGER__ = true;
  axios.interceptors.request.use((cfg) => {
    const m = (cfg.method || "get").toUpperCase();
    console.log("HTTP →", m, cfg.url, cfg.params ? { params: cfg.params } : {}, cfg.data ? { data: cfg.data } : {});
    return cfg;
  });
}

export default function CreditNoteForm() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const mode = location.pathname.includes("/new") ? "create" : location.pathname.includes("/edit") ? "edit" : "view";
  const idNum = Number(id);
  const isCreate = mode === "create";
  const isEdit   = mode === "edit" && Number.isFinite(idNum) && idNum > 0;

  const [customer, setCustomer] = useState(null);
  const [date, setDate] = useState("");
  const [deduction, setDeduction] = useState("0");
  const [autoDeduction, setAutoDeduction] = useState(true);
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [checkingInv, setCheckingInv] = useState(false);
  const [customerHasInvoices, setCustomerHasInvoices] = useState(null);

  const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  // ---------- Async sources ----------
  const fetchCustomers = async (inputValue) => {
    const r = await axios.get(`${API_BASE_URL}/api/customers`, { params: { q: inputValue, limit: 20 }, headers: tokenHeader() });
    const arr = Array.isArray(r.data) ? r.data : r.data?.rows || [];
    return arr.map((c) => ({ value: c.id, label: c.name }));
  };

  // customer-scoped products
  const fetchProducts = async (customerId, inputValue) => {
    if (!customerId) return [];
    const r = await axios.get(
      `${API_BASE_URL}/api/credit/helpers/products`,
      { params: { customer_id: customerId, q: inputValue, limit: 30 }, headers: tokenHeader() }
    );
    const rows = Array.isArray(r.data) ? r.data : r.data?.rows || [];
    return rows.map((p) => ({ value: p.id, label: p.name }));
  };

  // customer & product scoped batches
  const fetchBatches = async (customerId, productId, inputValue) => {
    if (!customerId || !productId) return [];
    const r = await axios.get(
      `${API_BASE_URL}/api/credit/helpers/batches`,
      { params: { customer_id: customerId, product_id: productId, q: inputValue, limit: 50 }, headers: tokenHeader() }
    );
    let rows = Array.isArray(r.data) ? r.data : r.data?.rows || [];
    if (inputValue) {
      const iv = String(inputValue).toLowerCase();
      rows = rows.filter((b) => String(b.batch_no).toLowerCase().includes(iv));
    }
    return rows.map((b) => ({
      value: b.batch_no,
      label:
        `${b.batch_no}` +
        (b.expiry_date ? ` • exp ${new Date(b.expiry_date).toLocaleDateString()}` : "") +
        ` • sold ${b.qty_sold ?? 0}` +
        (Number.isFinite(Number(b.stock)) ? ` • stock ${b.stock}` : ""),
    }));
  };

  const fetchMatchingInvoiceItems = async ({ customerId, productId, batchNo }) => {
    const r = await axios.get(`${API_BASE_URL}/api/credit/helpers/matching-invoice-items`, {
      params: { customer_id: customerId, product_id: productId, batch_no: batchNo, limit: 10 },
      headers: tokenHeader(),
    });
    return Array.isArray(r.data) ? r.data : [];
  };

  // ---------- Load for edit ----------
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setLoading(true);
        const r = await axios.get(`${API_BASE_URL}/api/credit/${idNum}`, { headers: tokenHeader() });
        const h = r.data.header;

        setCustomer({ value: h.customer_id, label: h.customer_name });
        setDate(h.credit_note_date || "");
        setDeduction(String(h.deduction || 0));
        setAutoDeduction(false); // respect saved value
        setRemarks(h.remarks || "");

        setItems((r.data.items || []).map((it) => ({
          id: it.id,
          product_id: it.product_id,
          product_opt: it.product_id ? { value: it.product_id, label: it.product_name } : null,
          batch_no: it.batch_no,
          batch_opt: it.batch_no ? { value: it.batch_no, label: it.batch_no } : null,

          ref_invoice_no: it.invoice_no || "",
          invoice_date: it.invoice_date || null,
          quantity: it.quantity,
          free_qty: it.free_qty,
          unit_price: it.unit_price,
          gst_percent: it.gst_percent,
          discount_percent: it.discount_percent ?? 0,

          __scheme_qty: it.scheme_qty || it.quantity || null,
          __scheme_free: it.scheme_free || it.free_qty || null,

          __matches: [],
          __showMatches: false,
          __loadingMatches: false,
        })));
      } catch (err) {
        console.error("Failed to load credit note for edit:", err);
        alert("Failed to load credit note");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, idNum]);

  // ---------- When customer changes, check invoice presence ----------
  useEffect(() => {
    let cancelled = false;
    async function checkInvoicesForCustomer(custId) {
      if (!custId) { setCustomerHasInvoices(null); return; }
      try {
        setCheckingInv(true);
        const r = await axios.get(`${API_BASE_URL}/api/credit/helpers/invoices`, {
          params: { customer_id: custId, limit: 1 }, headers: tokenHeader(),
        });
        const list = Array.isArray(r.data) ? r.data : r.data?.rows || [];
        if (!cancelled) setCustomerHasInvoices((list?.length || 0) > 0);
      } catch (err) {
        console.error("Invoice check failed:", err);
        if (!cancelled) setCustomerHasInvoices(false);
      } finally {
        if (!cancelled) setCheckingInv(false);
      }
    }
    checkInvoicesForCustomer(customer?.value || null);
    return () => { cancelled = true; };
  }, [customer?.value]);

  // ---------- Totals (per-row discount applied) ----------
  const totals = useMemo(() => {
  const { subtotal, gst } = items.reduce(
    (acc, it) => {
      const qty  = Number(it?.quantity)   || 0;
      const rate = Number(it?.unit_price) || 0;
      const base = qty * rate; // <-- Subtotal per line (no discount)

      // discount (prefer %; fall back to absolute if provided)
      let discPct = Number(it?.discount_percent ?? it?.__match_discount_percent ?? 0);
      if (!Number.isFinite(discPct)) discPct = 0;

      let discAmt = base * (discPct / 100);
      if (discPct === 0 && base > 0) {
        const abs = Number(it?.__match_discount_abs);
        if (Number.isFinite(abs) && abs > 0) discAmt = Math.min(abs, base);
      }

      const taxableAfterDiscount = Math.max(0, base - discAmt);
      const gstPct = Number(it?.gst_percent) || 0;
      const lineGst = taxableAfterDiscount * (gstPct / 100);

      acc.subtotal += base;   // we will return this as `taxable`
      acc.gst      += lineGst;
      return acc;
    },
    { subtotal: 0, gst: 0 }
  );

  return { taxable: subtotal, gst };
}, [items]);


  // ---------- Auto Deduction ----------
  const suggestedDeduction = useMemo(() => {
    return items.reduce((sum, it) => {
      const qty = Number(it.quantity || 0);
      const rate = Number(it.unit_price || 0);
      let discPct = Number(it.discount_percent ?? it.__match_discount_percent ?? 0);
      if (!Number.isFinite(discPct)) discPct = 0;
      if (!discPct && Number.isFinite(it.__match_discount_abs) && qty * rate > 0) {
        discPct = (Number(it.__match_discount_abs) / (qty * rate)) * 100;
      }
      const discountOnReturn = qty * rate * (discPct / 100);
      return sum + discountOnReturn;
    }, 0);
  }, [items]);

  useEffect(() => {
    if (autoDeduction) {
      setDeduction(String(Number(suggestedDeduction).toFixed(2)));
    }
  }, [autoDeduction, suggestedDeduction]);

  const creditNoteAmount = useMemo(() => {
    return Number(totals.taxable) - Number(deduction || 0) + Number(totals.gst);
  }, [totals, deduction]);

  // ---------- Row helpers ----------
  const newBlankRow = () => ({
    product_id: null,
    product_opt: null,
    batch_no: "",
    batch_opt: null,
    ref_invoice_no: "",
    invoice_date: null,
    quantity: 0,
    free_qty: 0,
    unit_price: 0,
    gst_percent: 0,
    discount_percent: 0,
    __scheme_qty: null,
    __scheme_free: null,
    __matches: [],
    __showMatches: false,
    __loadingMatches: false,
  });

  const addRow = () => setItems((it) => [...it, newBlankRow()]);
  const addRowAfter = (idx, row = newBlankRow()) =>
    setItems((it) => {
      const arr = [...it];
      arr.splice(idx + 1, 0, row);
      return arr;
    });

  const duplicateRow = (idx, opts = {}) => {
    setItems((it) => {
      const src = it[idx];
      if (!src) return it;
      const cloned = { ...src, id: undefined, __matches: [], __showMatches: false, __loadingMatches: false, ...opts };
      const arr = [...it];
      arr.splice(idx + 1, 0, cloned);
      return arr;
    });
  };

  const delRow = (idx) => setItems((it) => it.filter((_, i) => i !== idx));
  const setRow  = (idx, patch) => setItems((it) => it.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  useEffect(() => { if (isCreate && items.length === 0) addRow(); /* eslint-disable-next-line */ }, [isCreate]);

  const fetchMatching = async (idx, { batchNo, productId } = {}) => {
    const row = items[idx];
    const pid = productId ?? row?.product_id;
    const bno = batchNo ?? row?.batch_no;
    if (!row || !customer?.value || !pid || !bno) {
      setRow(idx, { __matches: [], __showMatches: false, __loadingMatches: false });
      return;
    }
    try {
      setRow(idx, { __loadingMatches: true });
      const matches = await fetchMatchingInvoiceItems({ customerId: customer.value, productId: pid, batchNo: bno });
      setRow(idx, { __matches: matches, __showMatches: true });
    } finally {
      setRow(idx, { __loadingMatches: false });
    }
  };

  // ----- CORE: free ratio logic -----
  const int = (v) => Math.max(0, Math.floor(Number(v) || 0));
  const computeFreeFromQty = (qty, schemeQty, schemeFree) => {
    const q = int(qty);
    const baseQ = int(schemeQty);
    const baseF = int(schemeFree);
    if (q <= 0) return 0;
    if (baseQ > 0 && baseF > 0) {
      const block = baseQ / baseF;                 // units per 1 free
      const computed = Math.ceil(q / block);       // step buckets
      return Math.max(1, Math.min(baseF, computed)); // clamp to [1..baseF]
    }
    return 1; // fallback: at least 1 free when qty > 0
  };

  const applyMatch = (idx, m) => {
    let discPct = null;
    if (m.discount_percent != null) discPct = Number(m.discount_percent);
    const discountAbs =
      m.discount_amt != null ? Number(m.discount_amt)
      : m.discount != null   ? Number(m.discount)
      : null;

    const schemeQty  = int(m.quantity || 0);
    const schemeFree = int(m.free_qty || 0);

    setRow(idx, {
      ref_invoice_no: m.invoice_no || m.invoice_number || "",
      invoice_date: m.invoice_date || null,
      quantity: Number(m.quantity || 0),
      free_qty: Number(m.free_qty || 0),
      unit_price: Number(m.unit_price || 0),
      gst_percent: m.gst_percent != null ? Number(m.gst_percent) : Number(m.cgst || 0) + Number(m.sgst || 0),
      discount_percent: Number.isFinite(discPct) ? discPct : undefined,
      __match_discount_percent: Number.isFinite(discPct) ? discPct : undefined,
      __match_discount_abs: Number.isFinite(discountAbs) && discountAbs > 0 ? discountAbs : undefined,
      __scheme_qty: schemeQty || null,
      __scheme_free: schemeFree || null,
      __showMatches: false,
    });
  };

  const onQtyChange = (idx, raw) => {
    const q = int(raw);
    const row = items[idx];
    const newFree = computeFreeFromQty(q, row.__scheme_qty, row.__scheme_free);
    setRow(idx, { quantity: q, free_qty: newFree });
  };

  const onFreeChange = (idx, raw) => {
    let f = int(raw);
    const row = items[idx];
    const maxF = int(row.__scheme_free || row.free_qty || 0);
    if (maxF > 0) f = Math.min(f, maxF);
    if (int(row.quantity) > 0) f = Math.max(1, f); else f = 0;
    setRow(idx, { free_qty: f });
  };

  // ---------- Submit ----------
  const submit = async (e) => {
    e.preventDefault();
    if (!customer) return alert("Select customer");
    if (isCreate && customerHasInvoices === false) {
      alert("This customer has no invoices. You cannot create a credit note.");
      return;
    }

    const payload = {
      customer_id: customer.value,
      credit_note_date: date || null,
      deduction: Number(deduction || 0),
      remarks,
      items: items.map((r) => ({
        id: r.id,
        ref_invoice_no: r.ref_invoice_no || null,
        batch_no: r.batch_no,
        quantity: Number(r.quantity || 0),
        free_qty: Number(r.free_qty || 0),
        unit_price: Number(r.unit_price || 0),
        gst_percent: Number(r.gst_percent || 0),
        discount_percent: Number(r.discount_percent || r.__match_discount_percent || 0),
      })),
    };

    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/api/credit/${idNum}`, payload, { headers: tokenHeader() });
      } else {
        await axios.post(`${API_BASE_URL}/api/credit`, payload, { headers: tokenHeader() });
      }
      navigate("/creditNotes?page=1&page_size=20&sort_by=credit_note_date&sort_dir=desc");
    } catch (err) {
      console.error("Submit failed:", err);
      alert(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{isEdit ? "Edit" : "Create"} Credit Note</h1>
      </div>

      {isCreate && customer && customerHasInvoices === false && (
        <div className="rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2 text-sm">
          This customer has no invoices. You cannot create a credit note.
        </div>
      )}
      {isCreate && customer && checkingInv && (
        <div className="rounded-lg bg-amber-50 text-amber-800 border border-amber-200 px-3 py-2 text-sm">
          Checking invoices for this customer…
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-xl shadow">
          <div className="md:col-span-2">
            <label className="text-xs text-gray-500">Customer</label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              value={customer}
              loadOptions={fetchCustomers}
              onChange={(v) => {
                setCustomer(v);
                setCustomerHasInvoices(null);
                // clear rows when customer switches
                setItems([newBlankRow()]);
              }}
              styles={selectStylesProduct}
              classNamePrefix="sel"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Date</label>
            <input type="date" value={date || ""} onChange={(e) => setDate(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
          </div>
        </div>

        {/* Items — actions */}
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={addRow}
            className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm"
            disabled={!customer}
            title={!customer ? "Select customer first" : "Add new line"}
          >
            + Add Item
          </button>
        </div>

        {/* Items — table */}
        <div className="bg-white rounded-xl shadow overflow-auto max-h-[72vh] min-h-[420px]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left">
                <Th>#</Th>
                <Th style={{ minWidth: 250 }}>Product</Th>
                <Th style={{ minWidth: 200 }}>Batch</Th>
                <Th style={{ minWidth: 60 }}>Invoice #</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Free</Th>
                <Th className="text-right">Rate</Th>
                <Th className="text-right">GST %</Th>
                <Th style={{ width: 220 }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, idx) => (
                <FragmentRow
                  key={`${idx}-${r.product_id || "p"}-${r.batch_no || "b"}`}
                  idx={idx}
                  row={r}
                  customerId={customer?.value}
                  fetchProducts={fetchProducts}
                  fetchBatches={fetchBatches}
                  onRowChange={setRow}
                  onDelete={() => delRow(idx)}
                  onAddBlankAfter={() => addRowAfter(idx)}
                  onDuplicate={() => duplicateRow(idx)}
                  onAddAnotherBatch={() =>
                    // same product, blank batch, clear invoice/qty/free/price
                    duplicateRow(idx, {
                      batch_no: "",
                      batch_opt: null,
                      ref_invoice_no: "",
                      invoice_date: null,
                      quantity: 0,
                      free_qty: 0,
                      unit_price: r.unit_price, // keep rate (optional)
                      gst_percent: r.gst_percent, // keep gst (optional)
                      __matches: [],
                    })
                  }
                  onRefreshMatches={(opts) => fetchMatching(idx, opts)}
                  onApplyMatch={(m) => applyMatch(idx, m)}
                  onQtyChange={onQtyChange}
                  onFreeChange={onFreeChange}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Remarks (before totals/buttons) */}
        <div className="bg-white rounded-xl shadow p-4">
          <label className="text-xs text-gray-500">Remarks</label>
          <textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="optional" />
        </div>

        {/* Totals strip incl. auto Deduction */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Sum title="Taxable" value={totals.taxable} />
          <Sum title="GST" value={totals.gst} />
          <SumEditable
            title="Deduction"
            value={deduction}
            onChange={(val) => { setDeduction(val); setAutoDeduction(false); }}
            auto={autoDeduction}
            onToggleAuto={() => setAutoDeduction((v) => !v)}
          />
          <Sum title="Credit Note Amount" value={creditNoteAmount} />
        </div>

        <div className="flex gap-2 justify-end">
          <button disabled={loading} type="button" onClick={() => navigate("/credit")} className="px-3 py-2 rounded border hover:bg-gray-50">
            Cancel
          </button>
          <button disabled={loading || (isCreate && customerHasInvoices === false)} className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ------- Row -------
function FragmentRow({
  idx, row, customerId,
  fetchProducts, fetchBatches,
  onRowChange, onDelete, onAddBlankAfter, onDuplicate, onAddAnotherBatch,
  onRefreshMatches, onApplyMatch,
  onQtyChange, onFreeChange,
}) {
  const set = (patch) => onRowChange(idx, patch);

  const productLoader = async (input) => fetchProducts(customerId, input);
  const batchLoader   = async (input) => (!row.product_id ? [] : fetchBatches(customerId, row.product_id, input));

  return (
    <>
      <tr className="border-t align-top">
        <td className="p-3 text-gray-500">{idx + 1}</td>

        {/* Product */}
        <td className="p-2">
          <AsyncSelect
            cacheOptions
            defaultOptions
            isDisabled={!customerId}
            placeholder={customerId ? "Select product sold to this customer" : "Select customer first"}
            value={row.product_opt}
            loadOptions={productLoader}
            onChange={(opt) => {
              set({
                product_id: opt?.value || null,
                product_opt: opt || null,
                batch_no: "",
                batch_opt: null,
                __matches: [],
                __showMatches: false,
                __scheme_qty: null,
                __scheme_free: null,
              });
            }}
            styles={selectStylesProduct}
            classNamePrefix="sel"
          />
        </td>

        {/* Batch */}
        <td className="p-2">
          <AsyncSelect
            key={row.product_id || "no-prod"}
            cacheOptions
            defaultOptions
            isDisabled={!row.product_id || !customerId}
            placeholder={row.product_id ? "Select batch sold to this customer" : "Select product first"}
            value={row.batch_opt}
            loadOptions={batchLoader}
            onChange={(opt) => {
              set({ batch_no: opt?.value || "", batch_opt: opt || null, __matches: [] });
              if (customerId && row.product_id && opt?.value) {
                onRefreshMatches({ batchNo: opt.value, productId: row.product_id });
              }
            }}
            styles={selectStylesBatch}
            classNamePrefix="sel"
          />
          <div className="mt-1 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100">
              <span className="text-gray-600">Batch:</span>
              <span className="font-medium">{row.batch_no || "—"}</span>
            </span>
          </div>
        </td>

        {/* Invoice # */}
        <td className="p-2">
          <input
            value={row.ref_invoice_no || ""}
            onChange={(e) => set({ ref_invoice_no: e.target.value })}
            className="w-44 h-8 border rounded px-2 text-sm"
            placeholder="INV-..."
          />
          {row.invoice_date && (
            <div className="text-[11px] text-gray-500 mt-1">
              {new Date(row.invoice_date).toLocaleDateString()}
            </div>
          )}
        </td>

        {/* Qty */}
        <td className="p-2 text-right">
          <input
            type="number"
            value={row.quantity}
            onChange={(e) => onQtyChange(idx, e.target.value)}
            className="w-20 h-8 border rounded px-2 text-right text-sm"
          />
        </td>

        {/* Free */}
        <td className="p-2 text-right">
          <input
            type="number"
            value={row.free_qty}
            onChange={(e) => onFreeChange(idx, e.target.value)}
            className="w-20 h-8 border rounded px-2 text-right text-sm"
          />
        </td>

        {/* Rate */}
        <td className="p-2 text-right">
          <input
            type="number"
            step="0.01"
            value={row.unit_price}
            onChange={(e) => set({ unit_price: e.target.value })}
            className="w-20 h-8 border rounded px-2 text-right text-sm"
          />
        </td>

        {/* GST % */}
        <td className="p-2 text-right">
          <input
            type="number"
            step="0.01"
            value={row.gst_percent}
            onChange={(e) => set({ gst_percent: e.target.value })}
            className="w-20 h-8 border rounded px-2 text-right text-sm"
          />
        </td>

        {/* Actions */}
        <td className="p-2">
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              className="px-2 py-1 border rounded hover:bg-gray-50"
              onClick={() => {
                if (!customerId) return alert("Select customer first");
                if (!row.product_id) return alert("Select product first");
                if (!row.batch_no) return alert("Select batch first");
                onRefreshMatches({});
              }}
            >
              {row.__showMatches ? "Refresh matches" : "Find from invoices"}
            </button>

            <button type="button" className="px-2 py-1 border rounded hover:bg-gray-50" onClick={onAddBlankAfter}>
              + Row
            </button>

            <button type="button" className="px-2 py-1 border rounded hover:bg-gray-50" onClick={onDuplicate}>
              Duplicate
            </button>

            <button
              type="button"
              className="px-2 py-1 border rounded hover:bg-gray-50"
              onClick={onAddAnotherBatch}
              disabled={!row.product_id}
              title={!row.product_id ? "Pick product first" : "Add same product with another batch"}
            >
              + Batch
            </button>

            <button type="button" className="px-2 py-1 border rounded hover:bg-gray-50" onClick={onDelete}>
              Del
            </button>
          </div>
        </td>
      </tr>

      {/* Inline chooser */}
      {row.__showMatches && (
        <tr className="bg-slate-50">
          <td colSpan={9} className="p-2">
            {row.__loadingMatches ? (
              <div className="text-sm text-slate-600">Loading matching invoice items…</div>
            ) : (row.__matches?.length || 0) === 0 ? (
              <div className="text-sm text-slate-600">No matching invoice items for this customer/product/batch.</div>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-[920px] text-xs">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="p-2">Invoice #</th>
                      <th className="p-2">Date</th>
                      <th className="p-2 text-right">Qty</th>
                      <th className="p-2 text-right">Free</th>
                      <th className="p-2 text-right">Rate</th>
                      <th className="p-2 text-right">Disc%</th>
                      <th className="p-2 text-right">GST %</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {row.__matches.map((m) => (
                      <tr key={m.invoice_item_id} className="border-t">
                        <td className="p-2">{m.invoice_no || m.invoice_number}</td>
                        <td className="p-2">{m.invoice_date ? new Date(m.invoice_date).toLocaleDateString() : "-"}</td>
                        <td className="p-2 text-right">{m.quantity}</td>
                        <td className="p-2 text-right">{m.free_qty}</td>
                        <td className="p-2 text-right">{Number(m.unit_price || 0).toFixed(2)}</td>
                        <td className="p-2 text-right">{Number(m.discount_percent || 0).toFixed(2)}</td>
                        <td className="p-2 text-right">
                          {Number(
                            m.gst_percent != null ? m.gst_percent : Number(m.cgst || 0) + Number(m.sgst || 0)
                          ).toFixed(2)}
                        </td>
                        <td className="p-2">
                          <button type="button" className="px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => onApplyMatch(m)}>
                            Use
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Th({ children, className = "", ...rest }) {
  return <th className={`p-2 text-[11px] font-semibold text-gray-600 ${className}`} {...rest}>{children}</th>;

}
function Sum({ title, value }) {
  return (
    <div className="bg-white rounded-xl shadow p-4 text-right">
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-lg font-semibold">₹ {Number(value || 0).toFixed(2)}</div>
    </div>
  );
}
function SumEditable({ title, value, onChange, auto, onToggleAuto }) {
  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-start justify-between">
        <div className="text-xs text-gray-500">{title}</div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={!!auto} onChange={onToggleAuto} />
          Auto
        </label>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="text-lg font-semibold">₹ {Number(value || 0).toFixed(2)}</div>
        <input
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={auto}
          className={`w-28 border rounded px-2 py-1 text-right ${auto ? "bg-gray-100 text-gray-500" : ""}`}
          title="Deduction"
        />
      </div>
    </div>
  );
}

/** ---------- react-select styles ---------- */
/** ---------- react-select COMPACT styles ---------- */
const compactSelectBase = (minWidth) => ({
  container: (base) => ({ ...base, minWidth }),
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 8,
    boxShadow: "none",
    borderColor: state.isFocused ? "#60a5fa" : "#e5e7eb",
    ":hover": { borderColor: state.isFocused ? "#60a5fa" : "#d1d5db" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 5px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 32,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 4,
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: 4,
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    fontSize: "12px",
  }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
});

const selectStylesProduct = compactSelectBase("16rem"); // was 24rem
const selectStylesBatch   = compactSelectBase("14rem"); // was 20rem

